import React from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Lock } from 'lucide-react';

export function WeightAdjustmentContent({
  formData, handleFieldChange, errors, preventWheelChange,
  compact = false,
  locked = false
}) {
  const inputH = compact ? 'h-12 rounded-xl text-base' : 'h-10 rounded-lg';
  const lockedStyle = 'bg-slate-100 cursor-not-allowed opacity-70';
  
  if (locked) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
          <Lock className="w-4 h-4" />
          <span>This section is locked</span>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Weight Status</Label>
          <Input value={formData.weight_status === 'addition' ? 'Addition' : formData.weight_status === 'deduction' ? 'Deduction' : ''} readOnly className={`${inputH} ${lockedStyle}`} />
        </div>
        {formData.weight_status === 'addition' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">Additional Kgs</Label>
                <Input value={formData.additional_kgs} readOnly className={`${inputH} ${lockedStyle}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">Additional Tons</Label>
                <Input value={formData.additional_tons} readOnly className={`${inputH} ${lockedStyle}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">Additional Amount</Label>
                <Input value={formData.additional_invoice_amount ? `₹${formData.additional_invoice_amount}` : ''} readOnly className={`${inputH} ${lockedStyle}`} />
              </div>
            </div>
          </div>
        )}
        {formData.weight_status === 'deduction' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">Deduction Kgs</Label>
                <Input value={formData.deduction_kgs} readOnly className={`${inputH} ${lockedStyle}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">Deduction Tons</Label>
                <Input value={formData.deduction_tons} readOnly className={`${inputH} ${lockedStyle}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 ml-1">Deduction Amount</Label>
                <Input value={formData.deduction_invoice_amount ? `₹${formData.deduction_invoice_amount}` : ''} readOnly className={`${inputH} ${lockedStyle}`} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className={compact ? '' : 'space-y-2'}>
        {!compact && <Label className="text-sm font-semibold">Weight Status *</Label>}
        <Select value={formData.weight_status} onValueChange={(v) => handleFieldChange('weight_status', v)}>
          <SelectTrigger data-testid="weight-status-select" className={`${compact ? 'h-12 rounded-xl text-base' : 'h-11 rounded-xl'} ${formData.weight_status ? 'border-slate-900' : ''}`}>
            <SelectValue placeholder={compact ? 'Weight Status' : 'Select weight status'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="addition">Addition</SelectItem>
            <SelectItem value="deduction">Deduction</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {formData.weight_status === 'addition' && (
        <AdditionSection
          formData={formData} 
          handleFieldChange={handleFieldChange} 
          preventWheelChange={preventWheelChange} 
          compact={compact} 
          inputH={inputH}
        />
      )}

      {formData.weight_status === 'deduction' && (
        <DeductionSection
          formData={formData} 
          handleFieldChange={handleFieldChange} 
          preventWheelChange={preventWheelChange} 
          compact={compact} 
          inputH={inputH}
        />
      )}
    </div>
  );
}

function AdditionSection({ formData, handleFieldChange, preventWheelChange, compact, inputH }) {
  return (
    <div className="space-y-4">
      <div className={`grid ${compact ? 'grid-cols-2 gap-3' : 'grid-cols-3 gap-3'}`}>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Additional Kgs *</Label>
          <Input 
            data-testid="additional-kgs-input"
            type="number" 
            value={formData.additional_kgs} 
            onChange={(e) => handleFieldChange('additional_kgs', e.target.value)} 
            onWheel={preventWheelChange} 
            placeholder="0.00" 
            className={`${inputH} ${formData.additional_kgs ? 'border-green-500 bg-green-50' : ''}`} 
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Additional Tons *</Label>
          <Input 
            data-testid="additional-tons-input"
            type="number" 
            value={formData.additional_tons} 
            onChange={(e) => handleFieldChange('additional_tons', e.target.value)} 
            onWheel={preventWheelChange} 
            placeholder="0.000" 
            className={`${inputH} ${formData.additional_tons ? 'border-green-500 bg-green-50' : ''}`} 
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Amount *</Label>
          <Input 
            data-testid="additional-invoice-amount-input"
            type="number" 
            value={formData.additional_invoice_amount} 
            onChange={(e) => handleFieldChange('additional_invoice_amount', e.target.value)} 
            onWheel={preventWheelChange} 
            placeholder="0.00"
            className={`${inputH} ${formData.additional_invoice_amount ? 'border-green-500 bg-green-50' : ''}`} 
          />
          <p className="text-[10px] text-slate-500">Auto: rate x kgs + 5% GST</p>
        </div>
      </div>
      {!compact && (
        <div className="p-2 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-600">
            Rate: ₹{formData.rate || '0'}/kg
          </p>
        </div>
      )}
    </div>
  );
}

function DeductionSection({ formData, handleFieldChange, preventWheelChange, compact, inputH }) {
  return (
    <div className="space-y-4">
      <div className={`grid ${compact ? 'grid-cols-2 gap-3' : 'grid-cols-3 gap-3'}`}>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Deduction Kgs *</Label>
          <Input 
            data-testid="deduction-kgs-input"
            type="number" 
            value={formData.deduction_kgs} 
            onChange={(e) => handleFieldChange('deduction_kgs', e.target.value)} 
            onWheel={preventWheelChange} 
            placeholder="0.00" 
            className={`${inputH} ${formData.deduction_kgs ? 'border-green-500 bg-green-50' : ''}`} 
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Deduction Tons *</Label>
          <Input 
            data-testid="deduction-tons-input"
            type="number" 
            value={formData.deduction_tons} 
            onChange={(e) => handleFieldChange('deduction_tons', e.target.value)} 
            onWheel={preventWheelChange} 
            placeholder="0.000" 
            className={`${inputH} ${formData.deduction_tons ? 'border-green-500 bg-green-50' : ''}`} 
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Amount *</Label>
          <Input 
            data-testid="deduction-invoice-amount-input"
            type="number" 
            value={formData.deduction_invoice_amount} 
            onChange={(e) => handleFieldChange('deduction_invoice_amount', e.target.value)} 
            onWheel={preventWheelChange} 
            placeholder="0.00"
            className={`${inputH} ${formData.deduction_invoice_amount ? 'border-green-500 bg-green-50' : ''}`} 
          />
          <p className="text-[10px] text-slate-500">Auto: rate x kgs + 5% GST</p>
        </div>
      </div>
      {!compact && (
        <div className="p-2 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-600">
            Rate: ₹{formData.rate || '0'}/kg
          </p>
        </div>
      )}
    </div>
  );
}
